import { useSyncExternalStore } from 'react'

/**
 * Two questions the UI used to answer with one flag.
 *
 * `window.__CODEFLOWS_MOBILE` was set by the mobile bundle's api-shim and read
 * in fourteen places, but it was answering two unrelated questions at once:
 *
 *   "am I narrow?"   — should the file tree and its editor stack instead of
 *                      sitting side by side; should the sidebar overlay.
 *   "am I remote?"   — is there an OS window to minimise; can I flush
 *                      synchronously on quit.
 *
 * Conflating them meant the browser client got the phone layout on a 27"
 * monitor, and a half-width desktop window got the desktop layout it had no
 * room for. Worse, it made the layout untestable at a size: you could only
 * change it by changing which bundle you were in.
 *
 * So: layout is a media query, and only the genuinely platform-shaped
 * decisions keep a flag.
 */

/**
 * Below this the desktop layout stops fitting. The sidebar (196px) plus a
 * file tree (210px) plus a readable editor column is the binding constraint,
 * and it gives out around here — so this is where master/detail panes start
 * stacking and the sidebar becomes an overlay.
 *
 * Exported because the CSS side of the same decision has to agree with it.
 * That side is the `compact` screen in tailwind.config.js — `compact:` is a
 * `@media (max-width: 899px)` variant and must be kept equal to this number.
 * There is no `--breakpoint-compact` custom property and index.css declares
 * no @media rules; do not add a second place that encodes the breakpoint.
 *
 * Anything that needs the boundary in CSS uses `compact:`. Tailwind's stock
 * `max-md:` is 768px and must never be used for a compact-layout rule — the
 * 131px disagreement between the two silently mis-laid-out every iPad in
 * portrait until it was tracked down.
 */
export const COMPACT_MAX_WIDTH = 899

const COMPACT_QUERY = `(max-width: ${COMPACT_MAX_WIDTH}px)`

/**
 * One MediaQueryList for the whole app rather than one per component.
 *
 * Every consumer subscribes to the same object, so they all flip within the
 * same change event — no tearing, where a resize leaves the sidebar in its
 * compact form while the panel beside it is still laid out wide.
 */
const query: MediaQueryList | null =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(COMPACT_QUERY)
    : null

function subscribe(onChange: () => void): () => void {
  if (!query) return () => {}
  // addEventListener is the modern form; addListener is kept for the older
  // WebKit that some iOS home-screen webviews still run.
  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }
  query.addListener(onChange)
  return () => query.removeListener(onChange)
}

/**
 * True when the viewport is too narrow for the side-by-side desktop layout.
 *
 * Non-reactive: for effects, event handlers and anything outside render.
 * In render, use `useIsCompact` so the component re-renders on resize.
 */
export function isCompactViewport(): boolean {
  return query ? query.matches : false
}

/**
 * `isCompactViewport`, as a hook. Re-renders the caller when the viewport
 * crosses the breakpoint — including when a desktop window is simply dragged
 * narrow, which is the case the old flag could never express.
 */
export function useIsCompact(): boolean {
  return useSyncExternalStore(
    subscribe,
    isCompactViewport,
    // Server/prerender: assume roomy. The client corrects on first paint, and
    // guessing compact would flash the stacked layout on every desktop load.
    () => false,
  )
}

/**
 * True when this renderer is driving a desktop over the relay rather than
 * being the desktop.
 *
 * This is NOT a screen-size question and must never be used for layout — a
 * phone-shaped Electron window is still the desktop, and a browser on a large
 * monitor is still remote. It is for the handful of decisions that are about
 * the platform itself:
 *
 *   - window chrome: the traffic lights minimise a real OS window, and the
 *     relay would forward that to the machine on your desk, which is not what
 *     anyone tapping a browser expects;
 *   - the synchronous quit flush, which cannot cross a relay round-trip;
 *   - tab mounting, where only the active session is kept alive because the
 *     buffers are travelling over a socket.
 *
 * Feature-detecting `window.api` is often enough — but read `DESKTOP_ONLY` in
 * mobile/src/api-shim.ts before relying on it. It does NOT map desktop-only
 * methods to `undefined`; it installs `() => Promise.resolve(value)`, which is
 * a function whose return value is a truthy Promise. So `typeof api.x ===
 * 'function'` passes on mobile, and a caller that branches on the RESULT reads
 * that truthy Promise as success. Methods where that would be wrong are listed
 * in `OMITTED_ON_MOBILE` and genuinely absent; anything else needs this flag.
 */
export function isRemoteClient(): boolean {
  return typeof window !== 'undefined' && !!(window as { __CODEFLOWS_REMOTE?: boolean }).__CODEFLOWS_REMOTE
}
